import React, { useEffect, useState, Fragment } from 'react';
import { Button, Card, Collapse, Descriptions, Tag, Timeline } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import Loading from '../../components/loading';
import transactionService from '../../services/transaction';
import numberToPrice from '../../helpers/numberToPrice';

const MONEY_KEYS = new Set([
  'price',
  'order_payable_amount',
  'gateway_txn_amount',
  'coupon_discount_amount',
]);

const EVENT_LABELS = {
  transaction_started: 'Transaction started',
  payment_accepted: 'Payment accepted',
  payment_failed: 'Payment failed',
  provider_api_request: 'Provider request sent',
  provider_api_response: 'Provider response received',
  timeout: 'Timeout',
  refund_initiated: 'Refund initiated',
  receipt_generated: 'Receipt generated',
  manual_refund: 'Manual refund',
};

const PROVIDER_PAYLOAD_KEYS = new Set([
  'endpoint',
  'body_params',
  'request_parameters',
  'cn',
  'op',
  'cir',
  'amt',
  'reqid',
  'ad1',
  'ad2',
  'ad3',
  'ad4',
  'ad5',
  'paymentRefID',
  'remitterName',
  'customerMobile',
  'amount',
  'price',
  'qty',
  'sku',
  'pid',
  'telephone',
  'postcode',
  'region',
  'email',
  'firstname',
  'lastname',
  'connection_number',
  'operatorid',
  'circleid',
  'success',
  'status',
  'message',
  'errorCode',
  'errorMessage',
  'orderId',
  'data',
  'cards',
  'opRefNo',
  'txnId',
  'mobikwikstamp',
  'mobikwikTransactionId',
  'cardNumber',
  'cardPin',
  'expiryDate',
  'balance',
  'validity',
  'serialNo',
  'voucherCode',
  'voucherPin',
  'description',
]);

function formatLabel(key) {
  if (!key) return '';
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function filterProviderPayload(payload) {
  if (Array.isArray(payload)) {
    return payload
      .map(filterProviderPayload)
      .filter((item) => item !== null && item !== undefined);
  }
  if (isObject(payload)) {
    const result = {};
    Object.keys(payload).forEach((key) => {
      if (PROVIDER_PAYLOAD_KEYS.has(key)) {
        result[key] = filterProviderPayload(payload[key]);
      }
    });
    return result;
  }
  return payload;
}

function filterPayload(eventType, payload) {
  if (!payload) return payload;
  if (
    eventType === 'provider_api_request' ||
    eventType === 'provider_api_response'
  ) {
    return filterProviderPayload(payload);
  }
  return payload;
}

function FriendlyValue({ value }) {
  if (value === null || value === undefined) {
    return <span style={{ color: '#999' }}>-</span>;
  }

  if (typeof value === 'boolean') {
    return <Tag color={value ? 'green' : 'red'}>{value ? 'Yes' : 'No'}</Tag>;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return (
      <span style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{String(value)}</span>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span style={{ color: '#999' }}>Empty</span>;
    }
    return (
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {value.map((v, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            <FriendlyValue value={v} />
          </li>
        ))}
      </ul>
    );
  }

  if (isObject(value)) {
    return <ObjectTable obj={value} />;
  }

  return <span>{String(value)}</span>;
}

function ObjectTable({ obj, moneyKeys }) {
  if (!obj || !isObject(obj)) return null;
  const keys = Object.keys(obj);
  if (keys.length === 0) return <span style={{ color: '#999' }}>Empty</span>;

  return (
    <Descriptions bordered size='small' column={1}>
      {keys.map((key) => (
        <Descriptions.Item key={key} label={formatLabel(key)}>
          {moneyKeys?.has(key) && obj[key] !== null && obj[key] !== undefined ? (
            numberToPrice(Number(obj[key]) / 100, '₹')
          ) : (
            <FriendlyValue value={obj[key]} />
          )}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
}

export default function TransactionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function fetchTransaction(transactionId) {
    setLoading(true);
    setError(null);
    transactionService
      .getById(transactionId)
      .then((res) => {
        setData(res.data || res);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err?.message || 'Failed to load transaction');
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (id) {
      fetchTransaction(id);
    }
  }, [id]);

  return (
    <Card>
      <Button
        type='link'
        icon={<LeftOutlined />}
        onClick={() => navigate('/transactions')}
        style={{ marginBottom: 16, paddingLeft: 0 }}
      >
        {t('back')}
      </Button>

      {loading ? (
        <Loading />
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <Fragment>
          <ObjectTable
            moneyKeys={MONEY_KEYS}
            obj={Object.fromEntries(
              Object.entries(data).filter(([key]) => key !== 'timeline')
            )}
          />

          {data.timeline?.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ marginBottom: 16 }}>Transaction timeline</h4>
              <Timeline>
                {data.timeline.map((item) => {
                  const statusColor = {
                    success: 'success',
                    failed: 'error',
                    pending: 'warning',
                    info: 'default',
                  }[item.status] || 'default';

                  const dotColor = {
                    success: 'green',
                    failed: 'red',
                    pending: 'orange',
                    info: 'gray',
                  }[item.status] || 'gray';

                  const filteredPayload = filterPayload(
                    item.event_type,
                    item.payload
                  );
                  const hasPayload =
                    filteredPayload &&
                    Object.keys(filteredPayload).length > 0;
                  const hasMetadata =
                    item.metadata && Object.keys(item.metadata).length > 0;

                  return (
                    <Timeline.Item key={item.id} color={dotColor}>
                      <Card
                        size='small'
                        style={{ marginBottom: 12, borderRadius: 8 }}
                        bodyStyle={{ padding: 16 }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            gap: 12,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 16,
                                fontWeight: 600,
                                lineHeight: '22px',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                              }}
                            >
                              {EVENT_LABELS[item.event_type] ||
                                formatLabel(item.event_type)}
                            </div>
                            <span style={{ color: '#888', fontSize: 12 }}>
                              {item.created_at}
                            </span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            {item.status && (
                              <Tag color={statusColor}>{item.status}</Tag>
                            )}
                            {item.provider && <Tag>{item.provider}</Tag>}
                          </div>
                        </div>
                        {hasPayload || hasMetadata ? (
                          <Collapse ghost style={{ marginTop: 16 }}>
                            {hasPayload && (
                              <Collapse.Panel
                                header='Payload'
                                key='payload'
                              >
                                <FriendlyValue value={filteredPayload} />
                              </Collapse.Panel>
                            )}
                            {hasMetadata && (
                              <Collapse.Panel
                                header='Details'
                                key='metadata'
                              >
                                <FriendlyValue value={item.metadata} />
                              </Collapse.Panel>
                            )}
                          </Collapse>
                        ) : (
                          <p
                            style={{
                              margin: '12px 0 0',
                              color: '#888',
                              fontSize: 12,
                            }}
                          >
                            No additional details
                          </p>
                        )}
                      </Card>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            </div>
          )}
        </Fragment>
      )}
    </Card>
  );
}
